import { fabric } from "fabric";
import type { ITextboxOptions } from "fabric/fabric-impl";
import { omit } from "lodash";

export interface IWordWrapTextboxOptions extends ITextboxOptions {
  wrapChars?: string[];
  ellipsis?: boolean;
}

const DEFAULT_WRAP_CHARS = [" ", "-", "_", "/", "|"];

interface WrapConfig {
  desiredWidth: number;
  lineIndex: number;
  measureWord: fabric.Textbox["_measureWord"];
  wrapChars: string[];
  ellipsis: boolean;
}

export class WordWrapTextbox extends fabric.Textbox {
  private wrapChars: string[];
  private ellipsis: boolean;

  constructor(text: string, options?: IWordWrapTextboxOptions) {
    const textOptions = omit(options, "wrapChars", "ellipsis");
    super(text, textOptions);

    this.wrapChars = options?.wrapChars ?? DEFAULT_WRAP_CHARS;
    this.ellipsis = options?.ellipsis ?? false;
  }

  _wrapText(lines: string[], desiredWidth: number): string[][] {
    if (lines.length === 0) {
      return [[""]];
    }

    const wrappedLines: string[][] = [];
    const measureWord = this._measureWord.bind(this);

    for (let i = 0; i < lines.length; i++) {
      const config: WrapConfig = {
        desiredWidth,
        lineIndex: i,
        measureWord,
        wrapChars: this.wrapChars,
        ellipsis: this.ellipsis,
      };

      const lineWrappedParts = wrapSingleLine(lines[i], config);
      wrappedLines.push(...lineWrappedParts);
    }

    return wrappedLines.length ? wrappedLines : [[""]];
  }

  getWrapChars(): string[] {
    return this.wrapChars;
  }

  setOptions(options: IWordWrapTextboxOptions): void {
    if (options.wrapChars) {
      this.wrapChars = options.wrapChars;
    }

    if (options.ellipsis !== undefined) {
      this.ellipsis = options.ellipsis;
    }

    const textOptions = omit(options, "wrapChars", "ellipsis");
    super.setOptions(textOptions);
  }
}

const wrapSingleLine = (line: string, config: WrapConfig): string[][] => {
  const parts = splitTextByChars(line, config.wrapChars);
  const wrappedParts: string[][] = [];
  let currentLine = "";

  for (const part of parts) {
    const shouldWrap = shouldWrapLine(currentLine, part, config);

    if (shouldWrap) {
      const finalLine = applyEllipsis(currentLine, config);
      wrappedParts.push([finalLine]);
      currentLine = part;
    } else {
      currentLine += part;
    }
  }

  if (currentLine) {
    const finalLine = applyEllipsis(currentLine, config);
    wrappedParts.push([finalLine]);
  }

  return wrappedParts;
};

const shouldWrapLine = (
  currentLine: string,
  part: string,
  config: WrapConfig,
): boolean => {
  if (!currentLine) {
    return false;
  }

  const testLine = currentLine + part;
  const testWidth = config.measureWord([testLine], config.lineIndex, 0);

  return testWidth > config.desiredWidth;
};

const applyEllipsis = (text: string, config: WrapConfig): string => {
  return config.ellipsis
    ? truncateTextWithEllipsis(
        text,
        config.desiredWidth,
        config.lineIndex,
        config.measureWord,
      )
    : text;
};

const splitTextByChars = (text: string, wrapChars: string[]): string[] => {
  if (!text) {
    return [""];
  }

  if (!wrapChars || wrapChars.length === 0) {
    return [text];
  }

  const parts: string[] = [];
  let currentPart = "";

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    currentPart += char;

    if (wrapChars.includes(char) && i < text.length - 1) {
      parts.push(currentPart);
      currentPart = "";
    }
  }

  if (currentPart) {
    parts.push(currentPart);
  }

  return parts.length ? parts : [""];
};

const truncateTextWithEllipsis = (
  text: string,
  desiredWidth: number,
  lineIndex: number,
  measureWord: fabric.Textbox["_measureWord"],
): string => {
  const textWidth = measureWord([text], lineIndex, 0);

  if (textWidth <= desiredWidth) {
    return text;
  }

  const ellipsis = "...";
  let truncatedText = text;
  let length = text.length;

  while (length) {
    length--;
    truncatedText = text.substring(0, length).trim() + ellipsis;
    const textWidth = measureWord([truncatedText], lineIndex, 0);

    if (textWidth <= desiredWidth) {
      break;
    }
  }

  return truncatedText;
};
